/**
 * Les DEUX publics de la vitrine, et le contrat qui les tient
 * (SPEC-BOUTIQUE §8 ter E).
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LA RÉSERVE COMMERCIALE, ÉCRITE NOIR SUR BLANC DANS LA SPEC
 * ────────────────────────────────────────────────────────────────────────────
 *
 * « Ça élargit le marché et ça dilue le discours. L'accroche est forte *parce
 * qu'elle est précise*. Une entreprise qui organise une fête ne s'y
 * reconnaîtra pas ; un directeur de théâtre ne se reconnaîtra pas dans une page
 * qui parle de soirées d'entreprise. »
 *
 * D'où la décision : **deux entrées distinctes, pas un discours moyen**. Même
 * plateforme, deux promesses, deux parcours. Une seule page, mais chacun sait
 * en trois secondes laquelle des deux colonnes le concerne.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ★ CONTRAT AVEC LE LOT « LIEUX LIBRES ET ÉVÉNEMENTS PONCTUELS » (LIB / EVT)
 * ────────────────────────────────────────────────────────────────────────────
 *
 * La seconde promesse — le lieu à placement libre, le type de tenant
 * `evenement`, son inscription raccourcie — est livrée par un autre lot, en
 * parallèle. Ce fichier est **l'unique point de rendez-vous** entre les deux :
 * la vitrine n'a rien d'autre à changer le jour où cette porte s'ouvre.
 *
 * Deux gestes, et pas un de plus :
 *
 *   1. passer `disponible` à `true` sur `ENTREE_EVENEMENT` ;
 *   2. y poser le chemin de l'inscription raccourcie (`appel.chemin`).
 *
 * Tant que `disponible` vaut `false`, la carte le DIT — « nous préparons cette
 * offre, écrivez-nous » — et renvoie vers le formulaire de contact, qui existe
 * et fonctionne. Aucune promesse en avance de phase : une vitrine qui annonce
 * une fonction absente est exactement le défaut qu'on vient de réparer sur la
 * section « hors périmètre ».
 *
 * ★★ **RENDEZ-VOUS TENU — 21/08.** Le lot LIB/EVT a livré : les lieux à
 * placement libre vendent, et `/inscription/evenement` monte la billetterie
 * d'une soirée unique (EVT-02). Les deux gestes sont faits ci-dessous, et RIEN
 * d'autre n'a changé dans ce fichier — c'était tout l'intérêt du contrat. Un
 * test de `vitrine.test.tsx` bascule tout seul avec le booléen.
 */

/**
 * `/inscription` — écrit ici plutôt qu'importé de `features/inscription`.
 *
 * ARCHITECTURE §9.2 : aucune feature n'importe une autre feature. C'est le même
 * arbitrage que celui de `features/inscription/chemins.ts`, qui refuse d'écrire
 * dans `lib/chemins.ts` : une constante partagée pour une valeur qui n'intéresse
 * que deux modules coûte plus qu'elle ne rapporte. Le routeur monte
 * l'inscription sous ce chemin ; s'il change, ce fichier change.
 */
export const CHEMIN_ESSAI = '/inscription'

/**
 * ★ EVT-02 — `/inscription/evenement`, le parcours raccourci d'une soirée
 * unique. Même arbitrage que ci-dessus : la valeur est écrite ici, et son
 * jumeau vit dans `features/inscription/chemins.ts`
 * (`CHEMIN_INSCRIPTION_EVENEMENT`). Deux constantes, un seul chemin — c'est le
 * prix de la règle « aucune feature n'importe une autre feature », et il est
 * couvert par un test.
 */
export const CHEMIN_ESSAI_EVENEMENT = '/inscription/evenement'

/** Ancre du formulaire de contact, en bas de la même page. */
export const ANCRE_CONTACT = '#titre-contact'

export interface EntreeVitrine {
  readonly cle: 'saison' | 'evenement'
  readonly surtitre: string
  readonly titre: string
  readonly chapeau: string
  /** Ce que le produit fait pour CE public — trois points, jamais dix. */
  readonly points: readonly string[]
  /** La façon dont ce public paie. Une phrase, pas une grille. */
  readonly tarif: string
  /** `false` : l'offre est annoncée comme en préparation, pas comme livrée. */
  readonly disponible: boolean
  readonly appel: { readonly libelle: string; readonly chemin: string }
}

export const ENTREE_SAISON: EntreeVitrine = {
  cle: 'saison',
  surtitre: 'Votre salle, votre saison',
  titre: 'Théâtres et salles à programmation',
  chapeau:
    'Une saison, des abonnés, des places numérotées. Vous vendez toute l’année et vous rejouez chaque printemps la même campagne de réabonnement.',
  points: [
    'La billetterie complète : plan de salle, guichet, boutique en ligne, facture QR, contrôle d’accès, tableaux de bord.',
    'La campagne de réabonnement de bout en bout : propositions depuis la saison passée, sièges gelés jusqu’à l’ouverture publique, liste « à replacer » arbitrée une par une.',
    'Trois natures de formule : série imposée, N spectacles au choix, carnet d’entrées.',
  ],
  tarif: 'Forfait annuel par palier de jauge, sans commission sur vos recettes.',
  disponible: true,
  appel: { libelle: 'Essayer trente jours', chemin: CHEMIN_ESSAI },
}

export const ENTREE_EVENEMENT: EntreeVitrine = {
  cle: 'evenement',
  surtitre: 'Une date, un lieu',
  titre: 'Événements ponctuels',
  chapeau:
    'Un concert, une fête d’entreprise, une soirée en plein air. Pas de saison, pas d’abonnement : des entrées à vendre et une liste à contrôler à la porte.',
  points: [
    'Un lieu décrit par sa jauge plutôt que par un plan : on y choisit une quantité de places, pas un fauteuil.',
    'Le même parcours d’achat public, la même facture QR, le même contrôle d’accès au téléphone.',
    'Une interface allégée : ni campagne, ni formules, ni réabonnement à traverser.',
  ],
  tarif: 'Forfait par événement, sans engagement de douze mois.',
  /* ★ Voir le contrat en tête de fichier : ce booléen et le chemin ci-dessous
     sont les deux seules lignes que le lot LIB/EVT a à changer — et il les a
     changées le 21/08, l'offre étant livrée. */
  disponible: true,
  appel: { libelle: 'Créer ma billetterie', chemin: CHEMIN_ESSAI_EVENEMENT },
}

export const ENTREES: readonly EntreeVitrine[] = [ENTREE_SAISON, ENTREE_EVENEMENT]
